import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Modal, Empty, Spinner, useToast } from "../../components/ui";

const MENU = [["groups", "Groups"], ["locations", "Locations"], ["portals", "Portals"], ["payors", "Payors"], ["orders", "Orders"]];
const SECTION_META = {
  groups:    { label: "Groups",    sub: "Clients & production groups", color: "#6366f1", icon: "users" },
  locations: { label: "Locations", sub: "Clinic sites & codes",        color: "#0ea5e9", icon: "clipboard" },
  portals:   { label: "Portals",   sub: "Payor web portals",           color: "#8b5cf6", icon: "settings" },
  payors:    { label: "Payors",    sub: "Insurances / IPAs",           color: "#10b981", icon: "filter" },
  orders:    { label: "Orders",    sub: "Order catalog",               color: "#f59e0b", icon: "history" },
};

export function DataManagement() {
  const toast = useToast();
  const [section, setSection] = useState("groups");
  const [groups, setGroups] = useState(null);
  const [locations, setLocations] = useState(null);
  const [portals, setPortals] = useState(null);
  const [payors, setPayors] = useState(null);
  const [orders, setOrders] = useState(null);
  const [editing, setEditing] = useState(null);   // {type, row}
  const [confirm, setConfirm] = useState(null);    // {type, id, label}
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const [g, l, p, y, o] = await Promise.all([api.listGroups(), api.listRefLocations(), api.listPortals(), api.listPayors(), api.listOrders().catch(() => [])]);
      setGroups(g); setLocations(l); setPortals(p); setPayors(y); setOrders(o);
    } catch (e) { toast(e.message, "err"); setGroups([]); setLocations([]); setPortals([]); setPayors([]); setOrders([]); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const doDelete = async () => {
    const { type, id } = confirm;
    try {
      if (type === "groups") await api.deleteGroup(id);
      if (type === "locations") await api.deleteRefLocation(id);
      if (type === "portals") await api.deletePortal(id);
      if (type === "payors") await api.deletePayor(id);
      if (type === "orders") await api.deleteOrder(id);
      toast("Deleted", "ok"); setConfirm(null); load();
    } catch (e) { toast(e.message, "err"); setConfirm(null); }
  };

  const loading = groups === null || locations === null || portals === null || payors === null || orders === null;
  const singular = { groups: "group", locations: "location", portals: "portal", payors: "payor", orders: "order" }[section];

  // filter the active section by any column
  const match = (row, keys) => { const s = q.trim().toLowerCase(); if (!s) return true; return keys.some((k) => String(row[k] ?? "").toLowerCase().includes(s)); };
  const fGroups = (groups || []).filter((r) => match(r, ["name"]));
  const fLocations = (locations || []).filter((r) => match(r, ["sourceName", "code", "userGroup"]));
  const fPortals = (portals || []).filter((r) => match(r, ["name", "url"]));
  const fPayors = (payors || []).filter((r) => match(r, ["name", "portalName"]));
  const fOrders = (orders || []).filter((r) => match(r, ["name"]));

  const counts = { groups: fGroups.length, locations: fLocations.length, portals: fPortals.length, payors: fPayors.length, orders: fOrders.length };
  const sectionLabel = MENU.find((m) => m[0] === section)[1];

  return (
    <div className="dm2">
      <div className="dm-tabs">
        {MENU.map(([k]) => {
          const meta = SECTION_META[k];
          const total = ({ groups: (groups || []).length, locations: (locations || []).length, portals: (portals || []).length, payors: (payors || []).length, orders: (orders || []).length })[k];
          return (
            <button key={k} className={"dm-tab" + (k === section ? " on" : "")} onClick={() => { setSection(k); setQ(""); }}>
              {meta.label}<span className="dm-tab-n">{loading ? "…" : total}</span>
            </button>
          );
        })}
      </div>

      <div className="dm2-card">
        <div className="dm2-toolbar">
          <div className="dm2-title">{sectionLabel}<span className="dm2-count">{loading ? "…" : counts[section]}</span></div>
          <div className="dm2-search">
            <Icon.search width="15" height="15" />
            <input className="inp" placeholder={`Search ${singular}s…`} value={q} onChange={(e) => setQ(e.target.value)} />
            {q && <button className="ds-clear" onClick={() => setQ("")}><Icon.x width="13" height="13" /></button>}
          </div>
          <button className="btn btn-primary dm2-add" onClick={() => setEditing({ type: section, row: null })}><Icon.plus width="16" height="16" /> Add {singular}</button>
        </div>

        {loading ? <div className="empty"><Spinner /></div> : (
          <div className="dm2-grid-wrap">
            {section === "groups" && (
              <Grid cols={["Group name", "Actions"]} widths={["auto", "150px"]} rows={fGroups} empty={q ? "No groups match." : "No groups yet."}
                render={(r) => [<b>{r.name}</b>, <RowActions onEdit={() => setEditing({ type: "groups", row: r })} onDelete={() => setConfirm({ type: "groups", id: r.id, label: r.name })} />]} />
            )}
            {section === "locations" && (
              <Grid cols={["Location", "Code", "Group", "Actions"]} widths={["auto", "140px", "160px", "150px"]} rows={fLocations} empty={q ? "No locations match." : "No locations yet."}
                render={(r) => [r.sourceName, (r.code ? <span className="mono">{r.code}</span> : <span className="muted">—</span>), <span className="dm2-pill">{r.userGroup}</span>, <RowActions onEdit={() => setEditing({ type: "locations", row: r })} onDelete={() => setConfirm({ type: "locations", id: r.id, label: r.sourceName })} />]} />
            )}
            {section === "portals" && (
              <Grid cols={["Portal", "URL", "Actions"]} widths={["240px", "auto", "150px"]} rows={fPortals} empty={q ? "No portals match." : "No portals yet."}
                render={(r) => [<b>{r.name}</b>, <span className="cell-sub">{r.url || "—"}</span>, <RowActions onEdit={() => setEditing({ type: "portals", row: r })} onDelete={() => setConfirm({ type: "portals", id: r.id, label: r.name })} />]} />
            )}
            {section === "payors" && (
              <Grid cols={["Payor / IPA", "Portal", "Actions"]} widths={["auto", "200px", "150px"]} rows={fPayors} empty={q ? "No payors match." : "No payors yet."}
                render={(r) => [r.name, r.portalName ? <span className="dm2-pill">{r.portalName}</span> : <span className="cell-sub">— none —</span>, <RowActions onEdit={() => setEditing({ type: "payors", row: r })} onDelete={() => setConfirm({ type: "payors", id: r.id, label: r.name })} />]} />
            )}
            {section === "orders" && (
              <Grid cols={["Order name", "Actions"]} widths={["auto", "150px"]} rows={fOrders} empty={q ? "No orders match." : "No orders yet."}
                render={(r) => [<b>{r.name}</b>, <button className="link-btn danger" onClick={() => setConfirm({ type: "orders", id: r.id, label: r.name })}>Delete</button>]} />
            )}
          </div>
        )}
      </div>

      {editing && <EditRow ctx={editing} groups={groups || []} portals={portals || []} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {confirm && (
        <Modal title="Delete entry" onClose={() => setConfirm(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setConfirm(null)}>Cancel</button><button className="btn btn-danger" onClick={doDelete}>Delete</button></>}>
          <p>Delete <b>{confirm.label}</b>? This can't be undone.</p>
        </Modal>
      )}
    </div>
  );
}

function Grid({ cols, widths, rows, render, empty }) {
  if (!rows.length) return <Empty icon={<Icon.clipboard width="34" height="34" />} title={empty} text="Use the Add button to create one." />;
  return (
    <div className="dm2-grid-scroll">
      <table className="dm2-grid">
        <colgroup>{(widths || cols.map(() => "auto")).map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
        <thead><tr>{cols.map((c, i) => <th key={i} className={i === cols.length - 1 ? "ta-right" : ""}>{c}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>{render(r).map((cell, i) => <td key={i} className={i === cols.length - 1 ? "dm2-actions" : ""}>{cell}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowActions({ onEdit, onDelete }) {
  return <><button className="link-btn" onClick={onEdit}>Edit</button><button className="link-btn danger" onClick={onDelete}>Delete</button></>;
}

function EditRow({ ctx, groups, portals, onClose, onSaved }) {
  const toast = useToast();
  const { type, row } = ctx;
  const [form, setForm] = useState(() => {
    if (type === "groups") return { name: row?.name || "" };
    if (type === "locations") return { sourceName: row?.sourceName || "", code: row?.code || "", userGroup: row?.userGroup || (groups[0]?.name || "LACN") };
    if (type === "portals") return { name: row?.name || "", url: row?.url || "" };
    if (type === "orders") return { name: row?.name || "" };
    return { name: row?.name || "", portalId: row?.portalId || "" };
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setErr(""); setBusy(true);
    try {
      if (type === "groups") await api.saveGroup(row?.id, { name: form.name });
      else if (type === "locations") await api.saveRefLocation(row?.id, { kind: "LOCATION", sourceName: form.sourceName, code: form.code || "", userGroup: form.userGroup });
      else if (type === "portals") await api.savePortal(row?.id, form);
      else if (type === "orders") await api.addOrder(form.name);
      else await api.savePayor(row?.id, { name: form.name, portalId: form.portalId ? Number(form.portalId) : null });
      toast("Saved", "ok"); onSaved();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const singular = { groups: "group", locations: "location", portals: "portal", payors: "payor", orders: "order" }[type];
  return (
    <Modal title={(row ? "Edit " : "Add ") + singular} onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? <span className="spin" /> : "Save"}</button></>}>
      {type === "groups" && (
        <div className="field"><label>Group name</label><input className="inp" value={form.name} onChange={set("name")} placeholder="e.g. LACN" /></div>
      )}
      {type === "locations" && (
        <>
          <div className="field"><label>Location (exact value from the export)</label><input className="inp" value={form.sourceName} onChange={set("sourceName")} placeholder="e.g. Anaheim" /></div>
          <div className="field"><label>Location code</label><input className="inp" value={form.code} onChange={set("code")} placeholder="e.g. ANH" /></div>
          <div className="field"><label>Group</label>
            <select className="inp" value={form.userGroup} onChange={set("userGroup")}>
              {groups.map((g) => <option key={g.id} value={g.name}>{g.name}</option>)}
            </select></div>
        </>
      )}
      {type === "portals" && (
        <>
          <div className="field"><label>Portal name</label><input className="inp" value={form.name} onChange={set("name")} placeholder="e.g. Availity" /></div>
          <div className="field"><label>URL (optional)</label><input className="inp" value={form.url} onChange={set("url")} placeholder="https://…" /></div>
        </>
      )}
      {type === "payors" && (
        <>
          <div className="field"><label>Payor name (must match Primary insurance)</label><input className="inp" value={form.name} onChange={set("name")} placeholder="e.g. Medicare (Medicare)" /></div>
          <div className="field"><label>Portal</label>
            <select className="inp" value={form.portalId} onChange={set("portalId")}>
              <option value="">— none —</option>
              {portals.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></div>
        </>
      )}
      {type === "orders" && (
        <div className="field"><label>Order name</label><input className="inp" value={form.name} onChange={set("name")} placeholder="e.g. PET/CT Skull to Thigh" /></div>
      )}
      {err && <div className="err-text">{err}</div>}
    </Modal>
  );
}